import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import cytoscape, { Core, ElementDefinition, NodeSingular, LayoutOptions } from 'cytoscape';
import coseBilkent from 'cytoscape-cose-bilkent';
import { useAppContext } from '../../context/AppContext';
import { useSiteGeoJSON } from '../../data/siteGeoJSON';
import { ChevronUp, ChevronDown, Download } from 'lucide-react';

// Register the layout extension
cytoscape.use(coseBilkent);

type LayoutType = 'breadthfirst' | 'circle' | 'cose' | 'grid';

const NetworkGraphTab: React.FC = () => {
  const { state, setSelectedComponents } = useAppContext();
  const { geoJSON, loading, error } = useSiteGeoJSON();
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<Core | null>(null);
  const nodePositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const isInitializedRef = useRef(false);
  const [isLegendCollapsed, setIsLegendCollapsed] = useState(false);
  const [selectedLayout, setSelectedLayout] = useState<LayoutType>('circle');
  const [isRelayouting, setIsRelayouting] = useState(false);

  // Build cytoscape elements from combiner labels (without selection state)
  const cytoscapeElements = useMemo(() => {
    if (!geoJSON) return [];

    // Extract combiner labels from GeoJSON
    const combinerFeatures = geoJSON.features.filter(
      (feature: any) => feature.properties.type === 'combiner_outlines' && feature.properties.label
    );

    const combinerLabels = combinerFeatures.map((feature: any) => feature.properties.label);
    
    // Parse label format: X1.X2.X3.C.X4 where X1=MV, X2=INV, X4=CMB
    const hierarchy = new Map();
    const elements: ElementDefinition[] = [];
    
    combinerLabels.forEach((label: string) => {
      const parts = label.split('.');
      if (parts.length >= 4 && parts[3] === 'C') {
        const mv = parts[0];
        const inv = parts[1]; 
        const cmb = parts[4];
        
        const mvId = mv; // Normalized ID: "2"
        const invId = `${mv}.${inv}`; // Normalized ID: "2.3"
        const cmbId = label; // Keep combiner as-is: "1.1.1.C.1"
        
        if (!hierarchy.has(mvId)) {
          hierarchy.set(mvId, { type: 'mv', label: `MV ${mv}`, inverters: new Set() });
        }
        
        if (!hierarchy.has(invId)) {
          hierarchy.set(invId, { type: 'inv', label: `INV ${mv}.${inv}`, combiners: new Set(), mv: mvId });
        }
        
        hierarchy.get(mvId).inverters.add(invId);
        hierarchy.get(invId).combiners.add(cmbId);
        hierarchy.set(cmbId, { type: 'cmb', label: label, inv: invId });
      }
    });

    // Build nodes with saved positions if available
    Array.from(hierarchy.entries()).forEach(([id, data]) => {
      const savedPosition = nodePositionsRef.current.get(id);
      
      elements.push({
        data: {
          id,
          label: data.label,
          type: data.type
        },
        position: savedPosition,
        classes: data.type
      });
    });

    // Build edges
    Array.from(hierarchy.values()).forEach((item) => {
      if (item.type === 'mv') {
        // MV to INV connections
        Array.from(item.inverters).forEach((invId: unknown) => {
          const invIdStr = invId as string;
          const mvId = Array.from(hierarchy.keys()).find(key => hierarchy.get(key) === item) as string;
          elements.push({
            data: {
              id: `${mvId}-${invIdStr}`,
              source: mvId,
              target: invIdStr,
              type: 'ac'
            },
            classes: 'ac-edge'
          });
        });
      } else if (item.type === 'inv') {
        // INV to CMB connections
        Array.from(item.combiners).forEach((cmbId: unknown) => {
          const cmbIdStr = cmbId as string;
          const invId = Array.from(hierarchy.keys()).find(key => hierarchy.get(key) === item) as string;
          elements.push({
            data: {
              id: `${invId}-${cmbIdStr}`,
              source: invId,
              target: cmbIdStr,
              type: 'dc'
            },
            classes: 'dc-edge'
          });
        });
      }
    });

    return elements;
  }, [geoJSON]);

  // Save node positions after layout
  const saveNodePositions = useCallback(() => {
    if (!cyRef.current) return;
    
    const positions = new Map<string, { x: number; y: number }>();
    cyRef.current.nodes().forEach((node: NodeSingular) => {
      const pos = node.position();
      positions.set(node.id(), { x: pos.x, y: pos.y });
    });
    nodePositionsRef.current = positions;
  }, []);

  // Get layout configuration based on selected type
  const getLayoutConfig = useCallback((layoutType: LayoutType): LayoutOptions => {
    const baseConfig = {
      fit: true,
      padding: 50,
      animate: false,
      boundingBox: undefined, // Let layout calculate its own bounds
      stop: () => {
        if (cyRef.current && !cyRef.current.destroyed()) {
          cyRef.current.fit();
          saveNodePositions();
          setIsRelayouting(false);
        }
      }
    };

    switch (layoutType) {
      case 'breadthfirst':
        return {
          ...baseConfig,
          name: 'breadthfirst',
          directed: true,
          spacingFactor: 2,
          avoidOverlap: true,
          nodeDimensionsIncludeLabels: true,
          grid: false,
          circle: false,
          roots: undefined // auto-detect roots
        };
      
      case 'circle':
        return {
          ...baseConfig,
          name: 'circle',
          avoidOverlap: true,
          nodeDimensionsIncludeLabels: true,
          radius: undefined, // Let it auto-calculate based on node count
          startAngle: 0,
          clockwise: true,
          sort: undefined,
          animate: false,
          animationDuration: 0,
          spacingFactor: 1
        };
      
      case 'cose':
        return {
          ...baseConfig,
          name: 'cose-bilkent',
          // Node repulsion settings
          nodeRepulsion: 4500,
          idealEdgeLength: 150,
          // Incremental cooling settings
          coolingFactor: 0.95,
          initialTemp: 200,
          // Gravity settings
          gravity: 0.25,
          // Layout settings
          nestingFactor: 0.1,
          numIter: 2500,
          // Performance settings
          tile: true,
          tilingPaddingVertical: 10,
          tilingPaddingHorizontal: 10,
          // Animation
          animate: false,  // Disable animation to prevent position issues
          // Randomization - only randomize if no positions saved
          randomize: nodePositionsRef.current.size === 0,
          nodeDimensionsIncludeLabels: false
        } as any;  // Type assertion for cose-bilkent specific options
      
      case 'grid':
        return {
          ...baseConfig,
          name: 'grid',
          avoidOverlap: true,
          avoidOverlapPadding: 10,
          nodeDimensionsIncludeLabels: false,
          spacingFactor: 1.5,
          condense: false,
          rows: undefined, // auto calculate
          cols: undefined // auto calculate
        };
      
      default:
        return {
          ...baseConfig,
          name: 'breadthfirst'
        };
    }
  }, [saveNodePositions]);

  // Custom hierarchical circle layout
  const applyHierarchicalCircleLayout = useCallback(() => {
    if (!cyRef.current || cyRef.current.destroyed()) return;

    
    const nodes = cyRef.current.nodes();
    const center = { x: 0, y: 0 };
    const baseRadius = 60;
    const ringSpacing = 50;
    const hierarchySpacing = 150; // Extra spacing between hierarchy levels

    // Group nodes by hierarchy level and convert to arrays
    const mvNodes = nodes.filter('[type = "mv"]').nodes().toArray();
    const invNodes = nodes.filter('[type = "inv"]').nodes().toArray();
    const cmbNodes = nodes.filter('[type = "cmb"]').nodes().toArray();


    // Use batch mode to prevent intermediate updates
    cyRef.current.startBatch();

    // Calculate optimal rings based on circumference
    const calculateRingsForNodes = (nodeCount: number, startRadius: number) => {
      const rings = [];
      let currentRadius = startRadius;
      let remainingNodes = nodeCount;
      
      while (remainingNodes > 0) {
        // Calculate circumference and max nodes for this radius
        const circumference = 2 * Math.PI * currentRadius;
        const nodeSpacing = 40; // Minimum spacing between nodes
        const maxNodesThisRing = Math.floor(circumference / nodeSpacing);
        const nodesThisRing = Math.min(remainingNodes, Math.max(1, maxNodesThisRing));
        
        rings.push({
          radius: currentRadius,
          nodeCount: nodesThisRing
        });
        
        remainingNodes -= nodesThisRing;
        currentRadius += ringSpacing * 0.8; // Closer rings
      }
      
      return rings;
    };

    // Helper function to position nodes in a ring
    const positionNodesInRing = (nodesList: any[], radius: number, startAngle: number = 0) => {
      nodesList.forEach((node, index) => {
        const angle = startAngle + (2 * Math.PI * index) / nodesList.length;
        const x = center.x + radius * Math.cos(angle);
        const y = center.y + radius * Math.sin(angle);
        node.position({ x, y });
      });
    };

    let currentNodeIndex = 0;
    let nextStartRadius = baseRadius;

    // Position MV nodes (center or innermost rings)
    let mvRings = [];
    if (mvNodes.length > 0) {
      if (mvNodes.length === 1) {
        mvNodes[0].position(center);
        nextStartRadius = baseRadius * 0.4 + hierarchySpacing; // Small radius + spacing for next level
      } else {
        mvRings = calculateRingsForNodes(mvNodes.length, baseRadius * 0.4);
        
        currentNodeIndex = 0;
        mvRings.forEach(ring => {
          const ringNodes = mvNodes.slice(currentNodeIndex, currentNodeIndex + ring.nodeCount);
          positionNodesInRing(ringNodes, ring.radius, 0);
          currentNodeIndex += ring.nodeCount;
        });
        
        // Set next start radius with hierarchy spacing
        const lastMvRadius = mvRings[mvRings.length - 1].radius;
        nextStartRadius = lastMvRadius + hierarchySpacing;
      }
    }

    // Position INV nodes (middle rings)
    let invRings = [];
    if (invNodes.length > 0) {
      const invStartRadius = Math.max(nextStartRadius, baseRadius + ringSpacing);
      invRings = calculateRingsForNodes(invNodes.length, invStartRadius);
      
      currentNodeIndex = 0;
      invRings.forEach(ring => {
        const ringNodes = invNodes.slice(currentNodeIndex, currentNodeIndex + ring.nodeCount);
        positionNodesInRing(ringNodes, ring.radius, 0);
        currentNodeIndex += ring.nodeCount;
      });
      
      // Set next start radius with hierarchy spacing
      const lastInvRadius = invRings[invRings.length - 1].radius;
      nextStartRadius = lastInvRadius + hierarchySpacing;
    }

    // Position CMB nodes (outer rings)
    if (cmbNodes.length > 0) {
      const cmbStartRadius = Math.max(nextStartRadius, baseRadius + (2 * ringSpacing));
      const cmbRings = calculateRingsForNodes(cmbNodes.length, cmbStartRadius);
      
      currentNodeIndex = 0;
      cmbRings.forEach(ring => {
        const ringNodes = cmbNodes.slice(currentNodeIndex, currentNodeIndex + ring.nodeCount);
        positionNodesInRing(ringNodes, ring.radius, 0);
        currentNodeIndex += ring.nodeCount;
      });
    }

    cyRef.current.endBatch();

    // Fit and save positions
    setTimeout(() => {
      if (cyRef.current && !cyRef.current.destroyed()) {
        cyRef.current.fit();
        saveNodePositions();
        setIsRelayouting(false);
      }
    }, 100);
  }, [saveNodePositions]);

  // Apply new layout
  const applyLayout = useCallback((layoutType: LayoutType) => {
    if (!cyRef.current || cyRef.current.destroyed()) return;
    
    setIsRelayouting(true);
    
    // Stop any running layout
    cyRef.current.stop();
    
    // Reset viewport before applying new layout
    cyRef.current.reset();
    
    // Special handling for different layout types
    if (layoutType === 'circle') {
      // Use custom hierarchical circle layout
      applyHierarchicalCircleLayout();
      return;
    }
    
    // Get fresh layout config
    const layoutConfig = getLayoutConfig(layoutType);
    
    // Special handling for breadthfirst vertical orientation
    if (layoutType === 'breadthfirst') {
      const layout = cyRef.current.layout({
        ...layoutConfig,
        transform: (node: any, position: { x: number; y: number }) => {
          // Rotate 90 degrees to make vertical
          return { x: -position.y, y: position.x };
        },
        stop: () => {
          if (cyRef.current && !cyRef.current.destroyed()) {
            cyRef.current.fit();
            saveNodePositions();
            setIsRelayouting(false);
          }
        }
      });
      layout.run();
    } else {
      // For other layouts, ensure they run with proper initialization
      const layout = cyRef.current.layout(layoutConfig);
      
      // Add promise-based completion handling
      const layoutPromise = layout.promiseOn('layoutstop');
      layout.run();
      
      layoutPromise.then(() => {
        if (cyRef.current && !cyRef.current.destroyed()) {
          cyRef.current.fit();
          saveNodePositions();
          setIsRelayouting(false);
        }
      }).catch((err) => {
        console.error('Layout error:', err);
        setIsRelayouting(false);
      });
    }
  }, [getLayoutConfig, saveNodePositions, applyHierarchicalCircleLayout]);

  // Handle layout change
  const handleLayoutChange = useCallback((newLayout: LayoutType) => {
    if (!cyRef.current || isRelayouting) return;
    
    setSelectedLayout(newLayout);
    // Clear saved positions when changing layout
    nodePositionsRef.current.clear();
    
    // Apply layout directly
    applyLayout(newLayout);
  }, [applyLayout, isRelayouting]);

  // Export network graph data to JSON
  const exportGraphData = useCallback(() => {
    if (!cyRef.current || cyRef.current.destroyed()) return;

    try {
      const nodes = cyRef.current.nodes();
      const edges = cyRef.current.edges();

      // Extract node data with positions
      const nodesData = nodes.map((node: NodeSingular) => {
        const position = node.position();
        const data = node.data();
        return {
          id: data.id,
          label: data.label,
          type: data.type,
          position: {
            x: Math.round(position.x * 100) / 100, // Round to 2 decimal places
            y: Math.round(position.y * 100) / 100
          },
          classes: node.classes()
        };
      });

      // Extract edge data
      const edgesData = edges.map((edge: any) => {
        const data = edge.data();
        return {
          id: data.id,
          source: data.source,
          target: data.target,
          type: data.type,
          classes: edge.classes()
        };
      });

      // Create export object with metadata
      const exportData = {
        metadata: {
          exportedAt: new Date().toISOString(),
          layout: selectedLayout,
          nodeCount: nodesData.length,
          edgeCount: edgesData.length,
          selectedComponents: state.selectedComponents,
          version: '1.0'
        },
        nodes: nodesData,
        edges: edgesData,
        // Additional graph statistics
        statistics: {
          nodeTypes: {
            mv: nodesData.filter(n => n.type === 'mv').length,
            inv: nodesData.filter(n => n.type === 'inv').length,
            cmb: nodesData.filter(n => n.type === 'cmb').length
          },
          connectionTypes: {
            ac: edgesData.filter(e => e.type === 'ac').length,
            dc: edgesData.filter(e => e.type === 'dc').length
          }
        }
      };

      // Create and download file
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `solar-farm-network-graph-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up URL
      URL.revokeObjectURL(url);

      
    } catch (error) {
      console.error('Failed to export graph data:', error);
    }
  }, [selectedLayout, state.selectedComponents]);

  // Initialize Cytoscape only once
  useEffect(() => {
    if (!containerRef.current || cytoscapeElements.length === 0) return;
    
    // Check if already initialized with same structure
    if (cyRef.current && !cyRef.current.destroyed()) {
      const currentNodeCount = cyRef.current.nodes().length;
      const newNodeCount = cytoscapeElements.filter(el => !el.data.source).length;
      
      if (currentNodeCount === newNodeCount) {
        // Same structure, don't recreate
        return;
      }
      
      // Structure changed, need to recreate
      saveNodePositions(); // Save positions before destroying
      cyRef.current.destroy();
      cyRef.current = null;
      isInitializedRef.current = false;
    }
    
    if (isInitializedRef.current) return;

    // Clear saved positions to force fresh layout with normalized IDs
    nodePositionsRef.current.clear();
    const hasPositions = false;

    cyRef.current = cytoscape({
      container: containerRef.current,
      elements: cytoscapeElements,
      style: [
        // Simplified base node styles for performance
        {
          selector: 'node',
          style: {
            'background-color': '#1f2937',
            'border-color': '#374151',
            'border-width': 2,
            'label': 'data(label)',
            'text-valign': 'center',
            'text-halign': 'center',
            'color': '#f9fafb',
            'font-size': '10px',
            'min-width': '40px',
            'min-height': '40px',
            'width': 40,
            'height': 40,
            'shape': 'ellipse' // Simple shape for performance
          }
        },
        // MV nodes (purple) - simplified
        {
          selector: '.mv',
          style: {
            'background-color': '#7c3aed',
            'border-color': '#8b5cf6',
            'width': 50,
            'height': 50,
            'font-size': '12px'
          }
        },
        // INV nodes (green) - simplified
        {
          selector: '.inv',
          style: {
            'background-color': '#10b981',
            'border-color': '#34d399',
            'width': 45,
            'height': 45,
            'font-size': '11px'
          }
        },
        // CMB nodes (cyan) - simplified
        {
          selector: '.cmb',
          style: {
            'background-color': '#06b6d4',
            'border-color': '#22d3ee',
            'width': 35,
            'height': 35,
            'font-size': '9px'
          }
        },
        // Selected nodes
        {
          selector: '.selected',
          style: {
            'border-color': '#fbbf24',
            'border-width': 4,
            'overlay-color': '#fbbf24',
            'overlay-opacity': 0.2,
            'overlay-padding': 4
          }
        },
        // Faded nodes
        {
          selector: '.faded',
          style: {
            'opacity': 0.3
          }
        },
        // Base edge styles - simplified
        {
          selector: 'edge',
          style: {
            'width': 2,
            'line-color': '#374151',
            'target-arrow-color': '#374151',
            'target-arrow-shape': 'triangle',
            'curve-style': 'straight' // Straight lines for performance
          }
        },
        // AC edges (blue)
        {
          selector: '.ac-edge',
          style: {
            'line-color': '#3b82f6',
            'target-arrow-color': '#3b82f6',
            'width': 3
          }
        },
        // DC edges (green)
        {
          selector: '.dc-edge',
          style: {
            'line-color': '#10b981',
            'target-arrow-color': '#10b981',
            'width': 2,
            'opacity': 0.2
          }
        },
        // Faded edges
        {
          selector: '.faded-edge',
          style: {
            'opacity': 0.3
          }
        }
      ],
      layout: selectedLayout === 'circle' ? {
        name: 'preset' // We'll apply custom circle layout after initialization
      } : getLayoutConfig(selectedLayout),
      // Performance options
      minZoom: 0.1,
      maxZoom: 5,
      wheelSensitivity: 0.2,
      motionBlur: false,
      textureOnViewport: true,
      pixelRatio: 1
    });

    // Always apply layout since we cleared saved positions
    if (selectedLayout === 'circle') {
      // Apply custom circle layout immediately after initialization
      setTimeout(() => {
        applyHierarchicalCircleLayout();
      }, 50);
    } else {
      cyRef.current.one('layoutstop', () => {
        setTimeout(() => {
          saveNodePositions();
        }, 100); // Small delay to ensure layout is fully complete
      });
    }

    // Handle node selection
    cyRef.current.on('tap', 'node', (event) => {
      const node = event.target;
      const nodeId = node.id();
      const currentSelection = state.selectedComponents;
      
      // Allow selection of combiner (CMB), inverter (INV), and medium voltage (MV) nodes
      const nodeData = node.data();
      if (!['cmb', 'inv', 'mv'].includes(nodeData.type)) {
        return;
      }
      
      if (currentSelection.includes(nodeId)) {
        setSelectedComponents(currentSelection.filter(id => id !== nodeId));
      } else {
        setSelectedComponents([...currentSelection, nodeId]);
      }
    });

    // Handle background tap to clear selection
    cyRef.current.on('tap', (event) => {
      if (event.target === cyRef.current) {
        setSelectedComponents([]);
      }
    });

    isInitializedRef.current = true;

    return () => {
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
        isInitializedRef.current = false;
      }
    };
  }, [cytoscapeElements, setSelectedComponents, saveNodePositions, getLayoutConfig, selectedLayout, applyHierarchicalCircleLayout]);

  // Update node selection when state changes - optimized
  useEffect(() => {
    if (!cyRef.current || cyRef.current.destroyed()) return;
    

    // Use batch mode for better performance
    cyRef.current.startBatch();
    
    try {
      // Clear all previous classes efficiently
      cyRef.current.elements().removeClass('selected faded faded-edge');
      
      if (state.selectedComponents.length === 0) {
        cyRef.current.endBatch();
        return;
      }

      const selectedNodes = cyRef.current.collection();
      const connectedNodes = cyRef.current.collection();

      // Find selected nodes
      state.selectedComponents.forEach(componentId => {
        const node = cyRef.current!.getElementById(componentId);
        if (node.length > 0) {
          selectedNodes.merge(node);
          
          // Get connected nodes (simplified to 2 degrees for performance)
          const neighbors = node.neighborhood();
          connectedNodes.merge(neighbors);
          const secondDegree = neighbors.neighborhood();
          connectedNodes.merge(secondDegree);
        }
      });

      // Apply classes
      selectedNodes.addClass('selected');
      
      // Fade unconnected elements
      const relevantNodes = selectedNodes.union(connectedNodes);
      cyRef.current.nodes().difference(relevantNodes).addClass('faded');
      
      // Fade edges not connecting relevant nodes
      cyRef.current.edges().forEach(edge => {
        const source = edge.source();
        const target = edge.target();
        if (!relevantNodes.contains(source) || !relevantNodes.contains(target)) {
          edge.addClass('faded-edge');
        }
      });


    } catch (error) {
      console.warn('Selection update error:', error);
    } finally {
      cyRef.current.endBatch();
    }
  }, [state.selectedComponents]);

  // Show loading state
  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-engramiq-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-engramiq-light-grey font-medium">Loading...</div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue">
        <div className="text-center p-8">
          <div className="text-accent-red text-xl mb-2">⚠️</div>
          <div className="text-engramiq-light-grey font-medium mb-2">Failed to load network data</div>
          <div className="text-engramiq-bright-grey text-sm">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative bg-engramiq-dark-blue">
      <div ref={containerRef} className="w-full h-full" />
      
      {/* Controls Panel with Collapse */}
      <div className={`absolute top-4 right-4 bg-engramiq-dark-blue/95 backdrop-blur-sm rounded-lg shadow-engramiq-lg border border-engramiq-green/20 transition-all duration-300 ${
        isLegendCollapsed ? 'w-auto' : 'max-w-sm'
      }`}>
        {/* Header with collapse button */}
        <div className="flex items-center justify-between p-4 pb-0">
          <h3 className={`text-sm font-bold text-engramiq-light-grey ${isLegendCollapsed ? 'hidden' : ''}`}>
            Network Graph
          </h3>
          <button
            onClick={() => setIsLegendCollapsed(!isLegendCollapsed)}
            className="ml-2 p-1 hover:bg-engramiq-green/10 rounded transition-colors"
            title={isLegendCollapsed ? 'Expand legend' : 'Collapse legend'}
          >
            {isLegendCollapsed ? (
              <ChevronDown className="w-4 h-4 text-engramiq-green" />
            ) : (
              <ChevronUp className="w-4 h-4 text-engramiq-green" />
            )}
          </button>
        </div>
        
        {/* Collapsible content */}
        <div className={`${isLegendCollapsed ? 'hidden' : 'block'} p-4 pt-3`}>
          <div className="space-y-3">
            {/* Layout Selector */}
            <div>
              <h4 className="text-xs font-semibold text-engramiq-green mb-2">Layout</h4>
              <select
                value={selectedLayout}
                onChange={(e) => handleLayoutChange(e.target.value as LayoutType)}
                disabled={isRelayouting}
                className="w-full px-2 py-1 text-xs bg-engramiq-dark-blue border border-engramiq-green/30 rounded text-engramiq-light-grey focus:outline-none focus:border-engramiq-green disabled:opacity-50"
              >
                <option value="circle">Circle (Hierarchical Rings)</option>
                <option value="cose">COSE (Force-Directed)</option>
                <option value="breadthfirst">Breadth First (Vertical)</option>
                <option value="grid">Grid</option>
              </select>
              {isRelayouting && (
                <div className="text-xs text-engramiq-bright-grey mt-1">Applying layout...</div>
              )}
            </div>
            
            {/* Export Controls */}
            <div>
              <h4 className="text-xs font-semibold text-engramiq-green mb-2">Export</h4>
              <button
                onClick={exportGraphData}
                disabled={!cyRef.current || cyRef.current.destroyed()}
                className="w-full px-3 py-2 text-xs bg-engramiq-green/10 hover:bg-engramiq-green/20 border border-engramiq-green/30 hover:border-engramiq-green/50 rounded text-engramiq-light-grey flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Export network graph data as JSON"
              >
                <Download className="w-3 h-3" />
                Export Graph Data
              </button>
              <div className="text-xs text-engramiq-bright-grey mt-1">
                Downloads JSON with nodes, edges & positions
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-engramiq-green mb-2">Node Types</h4>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-3 bg-purple-600 rounded border border-purple-400"></div>
                  <span className="text-xs text-engramiq-light-grey">Medium Voltage (MV)</span>
                  <span className="text-xs text-engramiq-green font-bold">*</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-3 bg-green-600 rounded border border-green-400"></div>
                  <span className="text-xs text-engramiq-light-grey">Inverter (INV)</span>
                  <span className="text-xs text-engramiq-green font-bold">*</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-3 bg-cyan-600 rounded border border-cyan-400"></div>
                  <span className="text-xs text-engramiq-light-grey">Combiner (CMB)</span>
                  <span className="text-xs text-engramiq-green font-bold">*</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-engramiq-green mb-2">Connection Types</h4>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-1 bg-blue-500"></div>
                  <span className="text-xs text-engramiq-light-grey">AC Connection</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-1 bg-green-500 border-dashed border-t border-green-500"></div>
                  <span className="text-xs text-engramiq-light-grey">DC Connection</span>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 pt-2 border-t border-engramiq-green/20">
            <div className="text-xs text-engramiq-bright-grey">
              Interactive network visualization
            </div>
            <div className="text-xs text-engramiq-bright-grey mt-1">
              Click nodes to select • Click background to clear
            </div>
            <div className="text-xs text-engramiq-bright-grey mt-1">
              <span className="text-engramiq-green">*</span> = Selectable for cross-filtering
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetworkGraphTab;